import { isAggregateFunction, type AggregateFunction } from './aggregate.js';
import type {
  DatabaseFilterClause,
  DatabaseFilterOperator,
  DatabaseSortClause,
  DatabaseSortField,
} from './query-types.js';

export type DatabaseViewType = 'table' | 'board' | 'list' | 'calendar' | 'timeline';

/** Column id for the title column in columnWidths. */
export const TITLE_COLUMN_WIDTH_KEY = '__title__';

const MIN_COLUMN_WIDTH = 48;
const MAX_COLUMN_WIDTH = 800;

export type DatabaseView = {
  id: string;
  name: string;
  type: DatabaseViewType;
  filters: DatabaseFilterClause[];
  sorts: DatabaseSortClause[];
  groupBy: DatabaseSortField | null;
  /** Select column id used as board lanes when type is board. */
  boardColumnId?: string;
  /** Date column id used as calendar placement when type is calendar. */
  calendarColumnId?: string;
  /** Date column id used as timeline placement when type is timeline. */
  timelineColumnId?: string;
  /** Select or multi-select column used to color calendar and timeline items. */
  colorColumnId?: string;
  /** Per-column width in px; title uses TITLE_COLUMN_WIDTH_KEY. */
  columnWidths?: Record<string, number>;
  /** Hidden schema column ids (title cannot be hidden). */
  hiddenColumns?: string[];
  /** Sticky first (title) column when type is table. */
  frozenTitle?: boolean;
  /** Wrap cell text in table view. */
  wrapText?: boolean;
  /** Per-column footer aggregate functions (stored columns only). */
  columnAggregates?: Record<string, AggregateFunction>;
};

export type DatabaseViewConfig = {
  version: 1;
  views: DatabaseView[];
  activeViewId: string;
};

const VIEW_CONFIG_KEY = 'viewConfig';

const FILTER_OPERATORS = new Set<DatabaseFilterOperator>([
  'eq',
  'neq',
  'contains',
  'is_empty',
  'is_not_empty',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeSortField(raw: unknown): DatabaseSortField | null {
  if (raw === 'title' || raw === 'position') {
    return raw;
  }
  if (isRecord(raw) && typeof raw.propertyId === 'string' && raw.propertyId.length > 0) {
    return { propertyId: raw.propertyId };
  }
  return null;
}

function normalizeFilterClause(raw: unknown): DatabaseFilterClause | null {
  if (!isRecord(raw) || typeof raw.operator !== 'string') {
    return null;
  }
  if (!FILTER_OPERATORS.has(raw.operator as DatabaseFilterOperator)) {
    return null;
  }
  const propertyId =
    raw.propertyId === 'title'
      ? 'title'
      : typeof raw.propertyId === 'string' && raw.propertyId.length > 0
        ? raw.propertyId
        : undefined;
  return {
    propertyId,
    operator: raw.operator as DatabaseFilterOperator,
    value: raw.value,
  };
}

function normalizeColumnWidths(raw: unknown): Record<string, number> | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  const widths: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof key !== 'string' || key.length === 0) {
      continue;
    }
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) {
      continue;
    }
    widths[key] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(n)));
  }
  return Object.keys(widths).length > 0 ? widths : undefined;
}

function normalizeHiddenColumns(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const seen = new Set<string>();
  const hidden: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.length > 0 && !seen.has(item)) {
      seen.add(item);
      hidden.push(item);
    }
  }
  return hidden.length > 0 ? hidden : undefined;
}

function normalizeColumnAggregates(raw: unknown): Record<string, AggregateFunction> | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  const out: Record<string, AggregateFunction> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof key !== 'string' || key.length === 0) {
      continue;
    }
    if (typeof value === 'string' && isAggregateFunction(value)) {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeSortClause(raw: unknown): DatabaseSortClause | null {
  if (!isRecord(raw) || raw.direction !== 'asc' && raw.direction !== 'desc') {
    return null;
  }
  const field = normalizeSortField(raw.field);
  if (!field) {
    return null;
  }
  return { field, direction: raw.direction };
}

function normalizeView(raw: unknown, fallbackId: string): DatabaseView | null {
  if (!isRecord(raw) || typeof raw.name !== 'string') {
    return null;
  }
  const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : fallbackId;
  const type: DatabaseViewType =
    raw.type === 'board' ||
    raw.type === 'list' ||
    raw.type === 'calendar' ||
    raw.type === 'timeline'
      ? raw.type
      : 'table';
  const filters: DatabaseFilterClause[] = [];
  if (Array.isArray(raw.filters)) {
    for (const item of raw.filters) {
      const clause = normalizeFilterClause(item);
      if (clause) {
        filters.push(clause);
      }
    }
  }
  const sorts: DatabaseSortClause[] = [];
  if (Array.isArray(raw.sorts)) {
    for (const item of raw.sorts) {
      const clause = normalizeSortClause(item);
      if (clause) {
        sorts.push(clause);
      }
    }
  }
  const groupBy = raw.groupBy === null ? null : normalizeSortField(raw.groupBy) ?? null;
  const boardColumnId =
    typeof raw.boardColumnId === 'string' && raw.boardColumnId.length > 0
      ? raw.boardColumnId
      : undefined;
  const calendarColumnId =
    typeof raw.calendarColumnId === 'string' && raw.calendarColumnId.length > 0
      ? raw.calendarColumnId
      : undefined;
  const timelineColumnId =
    typeof raw.timelineColumnId === 'string' && raw.timelineColumnId.length > 0
      ? raw.timelineColumnId
      : undefined;
  const colorColumnId =
    typeof raw.colorColumnId === 'string' && raw.colorColumnId.length > 0
      ? raw.colorColumnId
      : undefined;
  const columnWidths = normalizeColumnWidths(raw.columnWidths);
  const hiddenColumns = normalizeHiddenColumns(raw.hiddenColumns);
  const frozenTitle = raw.frozenTitle === true ? true : undefined;
  const wrapText = raw.wrapText === true ? true : undefined;
  const columnAggregates = normalizeColumnAggregates(raw.columnAggregates);
  return {
    id,
    name: raw.name.trim() || 'View',
    type,
    filters,
    sorts,
    groupBy,
    boardColumnId,
    calendarColumnId,
    timelineColumnId,
    colorColumnId,
    columnWidths,
    hiddenColumns,
    frozenTitle,
    wrapText,
    columnAggregates,
  };
}

export function defaultViewConfig(createViewId: () => string): DatabaseViewConfig {
  const id = createViewId();
  return {
    version: 1,
    views: [
      {
        id,
        name: 'Table',
        type: 'table',
        filters: [],
        sorts: [],
        groupBy: null,
      },
    ],
    activeViewId: id,
  };
}

export function normalizeViewConfig(
  raw: unknown,
  createViewId: () => string,
): DatabaseViewConfig {
  if (!isRecord(raw)) {
    return defaultViewConfig(createViewId);
  }
  const views: DatabaseView[] = [];
  if (Array.isArray(raw.views)) {
    for (let i = 0; i < raw.views.length; i++) {
      const view = normalizeView(raw.views[i], createViewId());
      if (view) {
        views.push(view);
      }
    }
  }
  if (views.length === 0) {
    return defaultViewConfig(createViewId);
  }
  const activeViewId =
    typeof raw.activeViewId === 'string' && views.some((v) => v.id === raw.activeViewId)
      ? raw.activeViewId
      : views[0]!.id;
  return { version: 1, views, activeViewId };
}

export function readViewConfig(
  content: Record<string, unknown> | undefined,
  createViewId: () => string,
): DatabaseViewConfig {
  if (!content || !isRecord(content[VIEW_CONFIG_KEY])) {
    return defaultViewConfig(createViewId);
  }
  return normalizeViewConfig(content[VIEW_CONFIG_KEY], createViewId);
}

export function getActiveView(config: DatabaseViewConfig): DatabaseView {
  return config.views.find((v) => v.id === config.activeViewId) ?? config.views[0]!;
}

export function setActiveViewId(config: DatabaseViewConfig, viewId: string): DatabaseViewConfig {
  if (!config.views.some((v) => v.id === viewId)) {
    return config;
  }
  return { ...config, activeViewId: viewId };
}

export function updateView(
  config: DatabaseViewConfig,
  viewId: string,
  patch: Partial<Omit<DatabaseView, 'id'>>,
): DatabaseViewConfig {
  return {
    ...config,
    views: config.views.map((v) => (v.id === viewId ? { ...v, ...patch } : v)),
  };
}

export function addView(
  config: DatabaseViewConfig,
  view: Omit<DatabaseView, 'id'> & { id?: string },
  createViewId: () => string,
): DatabaseViewConfig {
  const id = view.id ?? createViewId();
  const next: DatabaseView = {
    id,
    name: view.name,
    type: view.type,
    filters: view.filters ?? [],
    sorts: view.sorts ?? [],
    groupBy: view.groupBy ?? null,
    boardColumnId: view.boardColumnId,
    calendarColumnId: view.calendarColumnId,
    timelineColumnId: view.timelineColumnId,
    colorColumnId: view.colorColumnId,
    columnWidths: view.columnWidths ? { ...view.columnWidths } : undefined,
    hiddenColumns: view.hiddenColumns ? [...view.hiddenColumns] : undefined,
    frozenTitle: view.frozenTitle,
    wrapText: view.wrapText,
  };
  return {
    version: 1,
    views: [...config.views, next],
    activeViewId: id,
  };
}

export function renameView(
  config: DatabaseViewConfig,
  viewId: string,
  name: string,
): DatabaseViewConfig {
  const trimmed = name.trim();
  if (!trimmed) {
    return config;
  }
  return updateView(config, viewId, { name: trimmed });
}

export function duplicateView(
  config: DatabaseViewConfig,
  viewId: string,
  createViewId: () => string,
): DatabaseViewConfig {
  const source = config.views.find((v) => v.id === viewId);
  if (!source) {
    return config;
  }
  const id = createViewId();
  const copy: DatabaseView = {
    ...source,
    id,
    name: `${source.name} copy`,
    filters: source.filters.map((f) => ({ ...f })),
    sorts: source.sorts.map((s) => ({ ...s })),
    columnWidths: source.columnWidths ? { ...source.columnWidths } : undefined,
    hiddenColumns: source.hiddenColumns ? [...source.hiddenColumns] : undefined,
    columnAggregates: source.columnAggregates ? { ...source.columnAggregates } : undefined,
  };
  return {
    version: 1,
    views: [...config.views, copy],
    activeViewId: id,
  };
}

export function deleteView(config: DatabaseViewConfig, viewId: string): DatabaseViewConfig {
  if (config.views.length <= 1) {
    return config;
  }
  const views = config.views.filter((v) => v.id !== viewId);
  const activeViewId =
    config.activeViewId === viewId ? views[0]!.id : config.activeViewId;
  return { version: 1, views, activeViewId };
}

/** groupBy for POST /query when rendering a board view. */
export function boardQueryGroupBy(view: DatabaseView): DatabaseSortField | null {
  if (view.type !== 'board' || !view.boardColumnId) {
    return null;
  }
  return { propertyId: view.boardColumnId };
}

/** groupBy for table view (explicit table grouping only). */
export function tableQueryGroupBy(view: DatabaseView): DatabaseSortField | null {
  if (view.type !== 'table') {
    return null;
  }
  return view.groupBy;
}

export function queryGroupByForView(view: DatabaseView): DatabaseSortField | null {
  if (view.type === 'board') {
    return boardQueryGroupBy(view);
  }
  if (view.type === 'table') {
    return tableQueryGroupBy(view);
  }
  return null;
}

/** Default query sorts when the view has none (calendar by date column). */
export function querySortsForView(view: DatabaseView): DatabaseSortClause[] {
  if (view.sorts.length > 0) {
    return view.sorts;
  }
  if (view.type === 'calendar' && view.calendarColumnId) {
    return [{ field: { propertyId: view.calendarColumnId }, direction: 'asc' }];
  }
  if (view.type === 'timeline' && view.timelineColumnId) {
    return [{ field: { propertyId: view.timelineColumnId }, direction: 'asc' }];
  }
  return [{ field: 'position', direction: 'asc' }];
}

export type DatabaseViewQueryPlan = {
  sorts: DatabaseSortClause[];
  /** Follow cursors until every matching row is loaded. */
  allRows: boolean;
};

/**
 * Calendar and timeline draw whatever rows the query returns. The API pages by
 * position (100 rows), so a date sort keeps only the oldest page and drops later
 * dates. These views query by position and load every page; placement stays client-side.
 */
export function queryPlanForView(view: DatabaseView): DatabaseViewQueryPlan {
  if (view.type === 'calendar' || view.type === 'timeline') {
    return {
      sorts: [{ field: 'position', direction: 'asc' }],
      allRows: true,
    };
  }
  return { sorts: querySortsForView(view), allRows: false };
}
